/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from 'axios';
import { defineStore } from 'pinia';
import { ProductTypes } from '@/common/enums';
import { FileType } from '@/views/assets/enums';
import { i18n, requests, api } from '@/common/plugins';
import { fetchAsset as fetchAssetWorker } from '@/common/workers';
import { INITIAL_DIALOG_SETUP } from '@/views/assets/constants/initialDialogSetup';
import { buildPathFromArray, removeFromAxiosCache, removeItemInArray, sortItems } from '@/common/utils';
import { Asset, AssetDialogConfig, AssetFolder, AssetOptimizeProgress, AssetProp, AssetUploadBody, AssetUploadResponse, LibraryGetOptions } from '@/views/assets/interfaces';

const { t } = i18n.global;

let loaded: string[] = [];
let failed: string[] = [];

fetchAssetWorker.onmessage = (msg) => {
  const { status, id } = msg.data;
  if (status === 'loaded') loaded.push(id);
  if (status === 'failed') failed.push(id);
};

export const useAssetsStore = defineStore('assets', {
  state: () => ({
    library: [] as Asset[],
    folders: [] as AssetFolder[],
    assetType: '',
    currentFolder: { id: null } as Partial<AssetFolder>,
    folderHistory: [] as AssetFolder[],
    folderNestedIndex: 0,
    cutAssets: [], // the cut item to be used for pasting
    currentlySelectedAssets: [],
    lastSelectedAsset: null,
    dialogOpen: false,
    renamingAsset: false,
    fileType: '',
    showMoveDialog: false,
    showEditDialog: false,
    // Teleport config
    dialogSettings: { ...INITIAL_DIALOG_SETUP },
    autoUpload: false,
    autoUploadFiles: null as FileList,
    productType: null,
  }),

  getters: {
    isPlay: (state) => state.productType === ProductTypes.PLAY,
    isCanvas: (state) => state.productType === ProductTypes.CANVAS,

    /**
     * Check statsById data of current company and create an array of
     * CompanyInsight to be showed in companyEdit view.
     * @param state - Current state.
     * @returns - Array of CompanyInsight[]
     */
    getSelectedAssetsAlphabetically: (state) => {
      if (state.currentlySelectedAssets.length === 0) return [];
      // Sort alphabetically
      return sortItems(state.currentlySelectedAssets, { sortBy: 'name', desc: false });
    },

    /**
     * Returns asset library path according to componentState
     * and folderHistory items.
     * @param state - Current stare
     * @returns - Asset library in string format
     */
    assetLibraryPath: (state) => {
      const rootPath = t('ASSET_LIBRARY');
      const { componentState } = state.dialogSettings;

      if (componentState === 'editor') return rootPath;

      return buildPathFromArray(
        state.folderHistory.map((i) => i.name),
        rootPath,
      );
    },
  },

  actions: {
    /**
     * Set options values to dialogSettings to update AssetDialog
     * values and enable show status.
     * @param options - Dialog options
     */
    showAssetDialog(options: Partial<AssetDialogConfig>) {
      Object.assign(this.dialogSettings, {
        ...options,
        show: true,
      });
    },

    /**
     * Reset dialogSettings with Initial config to close
     * AssetDialog component
     */
    hideAssetDialog() {
      Object.assign(this.dialogSettings, { ...INITIAL_DIALOG_SETUP });
    },

    /**
     * Make a GET request to get asset library.
     */
    async fetchAssetsLibrary(args: LibraryGetOptions, query = ''):Promise<void> {
      const { cacheName, parentId } = this.useCommonLibraryValues();
      let cacheRef = cacheName;

      if (query) {
        cacheRef = `${cacheRef}-${query.split(':').pop()}`;
      }
      const isSearch = !!query.includes('search');
      const parentUri = `&parentId=${parentId}`;
      const response = await requests.get(`/assets/library?${query || ''}${isSearch ? '' : parentUri}`, {
        id: `${cacheRef}`,
        params: { ...args },
      }).catch(() => ({}));

      this.library = response.items.sort() || [];
      this.folders = response.folders.map((f) => ({ ...f, isDirectory: true })) || [];
      // Sort by createdAt
      this.library = sortItems(this.library, { sortBy: 'createdAt', desc: false });
    },

    /**
     * function to create a local empty folder
     * @param name => Specific folder name
     */
    addEmptyFolderItem(name?: string): void {
      const { parentId } = this.useCommonLibraryValues();

      this.folders.unshift({
        id: 'new_folder',
        name: name || 'New Folder',
        parentId,
        isDirectory: true,
        createdAt: new Date(),
      });
    },

    /**
     * Remove empty folder from the store if created by accident.
     * @param id => Folder id
     */
    removeEmptyFolderItem(id: string): void {
      const assetIndex = this.folders.findIndex((i) => i.id === id);
      this.folders.splice(assetIndex, 1);
    },

    /**
     * Make a POST request to create a new asset folder, add the
     * folder to state.
     */
    async createFolder(name: string, folderId?: string): Promise<AssetFolder> {
      const { parentId, cacheName, currentCompanyId } = this.useCommonLibraryValues();
      const payload = {
        parentId: folderId || parentId,
        companyId: currentCompanyId || null,
        name,
      };
      const response = await requests.post(
        '/assets/folders',
        payload,
        {
          cache: {
            update: {
              [cacheName]: 'delete',
              [`${cacheName}-folder`]: 'delete',
            },
          },
        },
      );

      this.updateLibraryState(response, 'folders', 'new_folder');
      return response;
    },

    async renameAsset(payload: Asset, newName:string): Promise<void> {
      const { cacheName } = this.useCommonLibraryValues();
      const fileType = payload.mimeType.split('/')[0];

      const response = await requests.patch(
        `/assets/${payload.id}`,
        { name: newName },
        {
          cache: {
            update: {
              [cacheName]: 'delete',
              [`${cacheName}-${fileType}`]: 'delete',
            },
          },
        },
      );

      this.updateLibraryState(response, 'library', payload.id);
      // finally update the asset cache
      this.updateCache(cacheName, 'library');
      this.updateCache(`${cacheName}-${fileType}`, 'library');
    },

    async renameFolder(payload: AssetFolder, newName:string): Promise<void> {
      const { cacheName } = this.useCommonLibraryValues();

      const response = await requests.patch(
        `/assets/folders/${payload.id}`,
        { name: newName },
        {
          cache: {
            update: {
              [cacheName]: 'delete',
              [`${cacheName}-folder`]: 'delete',
            },
          },
        },
      );

      this.updateLibraryState(response, 'folders', payload.id);
      // finally update the asset cache
      this.updateCache(cacheName, 'folders');
      this.updateCache(`${cacheName}-folder`, 'folders');
    },

    /**
     * Make deletion in 'currentlySelectedAssets' if
     * item has been selected
     * @param id - Item id
     */
    deleteSpecificItem(id: string) {
      const itemIndex = this.currentlySelectedAssets.findIndex((a) => a.id === id);
      // Check if can be deleted
      if (itemIndex === -1) return;
      // Make delete action
      this.currentlySelectedAssets.splice(itemIndex, 1);
    },

    /**
     * Make DELETE request to delete specific asset
     * and reset current store.
     * @param payload => Asset object
     */
    async deleteAsset(payload: AssetProp): Promise<void> {
      const { cacheName } = this.useCommonLibraryValues();
      const fileType = payload?.mimeType?.split('/')[0];

      const response = await requests.delete(
        `/assets/${payload.id}`,
      );

      // Delete from 'currentlySelectedAssets'
      this.deleteSpecificItem(payload.id);
      // update the store to remove the asset
      const assetIndex = this.library
        .findIndex((i) => i.id === payload.id);

      if (assetIndex > -1) {
        this.library.splice(assetIndex, 1);
      }

      // finally update the asset cache
      this.updateCache(cacheName, 'library');
      this.updateCache(`${cacheName}-${fileType}`, 'library');

      // TODO => Check if its ok to remove selectetAsset here
      if (response) this.removeSelectedAsset(payload);

      return response;
    },

    // TODO => Check if we can combine 'deleteFolder' and 'deleteAsset' actions

    async deleteFolder(payload: AssetProp): Promise<void> {
      const { cacheName } = this.useCommonLibraryValues();

      const response = await requests.delete(
        `/assets/folders/${payload.id}`,
        {
          cache: {
            update: {
              [cacheName]: 'delete',
              [`${cacheName}-folder`]: 'delete',
            },
          },
        },
      );

      // update the store to remove the asset
      const assetIndex = this.folders
        .findIndex((i) => i.id === payload.id);
      this.folders.splice(assetIndex, 1);
      // Delete from 'currentlySelectedAssets'
      this.deleteSpecificItem(payload.id);
      // finally update the asset cache
      this.updateCache(cacheName, 'folders');
      // this is needed for filtering by type folder
      this.updateCache(`${cacheName}-folder`, 'folders');

      // TODO => Check if its ok to remove selectetAsset here
      if (response) this.removeSelectedAsset(payload);

      return response;
    },

    /**
     * Function for opening a folder, and checking if it's already
     * in the history.
     */
    setCurrentFolder(folder: AssetFolder): Promise<void> {
      // check if folder is in the history
      const itemIndex = this.folderHistory.findIndex((n) => n.id === folder.id);

      // update the current folder
      this.currentFolder = folder;

      // if the folder is the root folder then reset the folder history
      if (folder.id === null) {
        this.resetFolderHistory();
        return Promise.resolve();
      }

      // if the folder is already in the history, set the nested index to that folder index
      if (itemIndex !== -1) {
        this.folderHistory.length = itemIndex + 1;
        this.folderNestedIndex = itemIndex + 1;
        return Promise.resolve();
      }
      // add the folder to the history
      this.folderHistory.push(folder);
      this.folderNestedIndex += 1;
      return Promise.resolve();
    },

    /**
     * Delete the folder history and set nested index back to 0.
     */
    resetFolderHistory() {
      this.folderHistory.length = 0;
      this.folderNestedIndex = 0;
    },

    setMoveAsset(payload: Asset[]): void {
      this.cutAssets = payload;
    },

    removeSelectedAsset(asset: AssetProp): void {
      const assetIndex = this.currentlySelectedAssets.findIndex((i) => i.id === asset.id);
      if (assetIndex !== -1) {
        this.currentlySelectedAssets.splice(assetIndex, 1);
      }
      // update the cut list
      // if (this.cutAssets.length !== 0) this.setMoveAsset(this.currentlySelectedAssets);
    },

    /**
      * Function to sort assets by if they are a folder or not
      */
    sortAssetsByIsFolder(assets) {
      const assetList = [];
      const folderList = [];
      assets.forEach((asset) => {
        if ('isFile' in asset) {
          assetList.push(asset);
        } else {
          folderList.push(asset);
        }
      });

      return {
        assetList,
        folderList,
      };
    },

    /**
     * Make a GET request to get asset library.
     */
    async fetchAsset(id):Promise<any> {
      try {
        const asset = await requests.get(`/assets/${id}`);
        return asset;
      } catch {
        return Promise.resolve();
      }
    },

    /**
     * Function to select all assets between two assets when shift is pressed
     */
    shiftMultiSelectAsset(asset:Asset):void {
      // get the index of the last selected asset and current asset
      const start = this.library.findIndex((a) => a.id === this.lastSelectedAsset.id);
      const end = this.library.findIndex((a) => a.id === asset.id);

      // get all assets between the two indexes using min and max so selections can be made in any direction
      this.currentlySelectedAssets = this.library.slice(Math.min(start, end), Math.max(start, end) + 1);
      this.lastSelectedAsset = asset;
    },

    /**
     * Function to select all assets
     */
    selectAllAssets():void {
      this.currentlySelectedAssets = [...this.library];
    },

    /**
     * Helper function to cancel bulk actions.
     */
    cancelBulkActions() {
      this.setMoveAsset([]);
      this.currentlySelectedAssets = [];
    },

    /**
     * Helper function to update library state.
     */
    useCommonLibraryValues() {
      const { id } = this.currentFolder;
      const currentCompanyId = sessionStorage.getItem('cmp:company-id');
      const cacheName = `fetch-asset-library-${currentCompanyId}-${id}`;

      return {
        currentCompanyId,
        parentId: id,
        cacheName,
      };
    },

    /**
     * Helper function to update library state.
     */
    updateLibraryState(response, property, id) {
      const assetIndex = this[property]
        .findIndex((i) => i.id === id);

      this[property][assetIndex] = {
        ...this[property][assetIndex],
        ...response,
      };
    },

    /**
     * Helper function to update cached library state.
     */
    updateCache(name: string, property: string) {
      const cache = window.sessionStorage.getItem(`axios-cache:${name}`);
      if (!cache) return;

      const json = JSON.parse(cache);
      json.data.data.items = this[property];
      window.sessionStorage.setItem(`axios-cache:${name}`, JSON.stringify(json));
    },

    /**
     * Make a GET request to get asset folders of
     * specific company.
     */
    async fetchFolders(parentId: string) {
      const { currentCompanyId } = this.useCommonLibraryValues();

      const result = await requests.get('/assets/library', {
        id: `fetch-asset-folders-${currentCompanyId}-${parentId}`,
        params: { companies: currentCompanyId, parentId },
      }).catch(() => ({}));

      return result.folders || [];
    },

    async moveAsset(destinationId: string, item: Asset | AssetFolder) {
      const list = item.isDirectory ? 'folders' : 'items';
      const { cacheName } = this.useCommonLibraryValues();
      const url = `/assets${item.isDirectory ? '/folders' : ''}/${item.id}`;
      const body = item.isDirectory ? { parentId: destinationId } : { folderId: destinationId };

      await requests.patch(url, body, {
        cache: {
          update: {
            [cacheName]: (cache) => removeFromAxiosCache(cache, list, item.id),
          },
        },
      });

      // Remove items from currentParent
      if (item.isDirectory) {
        this.folders = removeItemInArray(this.folders, item.id) as AssetFolder[];
      } else {
        this.library = removeItemInArray(this.library, item.id) as Asset[];
      }
    },

    setFileType(type: FileType) {
      this.fileType = type;
    },

    /**
     * Updates 'showMoveDialog' to inverse value
     */
    toggleMoveDialog() {
      this.showMoveDialog = !this.showMoveDialog;
    },

    /**
     * Calls 'sortItems' utility to sort 'folders'
     * and 'library' items depends on 'option' value.
     * @param option - Key variable to sort
     * @param isDesc - Is descending mode ?
     */
    sortAssets(option: string, isDesc: boolean) {
      this.folders = sortItems(this.folders, { sortBy: option, desc: isDesc });
      this.library = sortItems(this.library, { sortBy: option, desc: isDesc });
    },

    /**
     * Updates 'showEditDialog' to inverse value
     */
    toggleEditDialog() {
      this.showEditDialog = !this.showEditDialog;
    },

    /**
     * Make POST request to add current asset and upload
     * the result to S3 using presigned URL.
     * @param data - Asset info (AssetUploadBody)
     * @param file - Asset file
     * @returns - Upload result
     */
    async uploadAsset(data: AssetUploadBody, file: File): Promise<AssetUploadResponse> {
      const preSignedResponse = await requests.post('/assets', data);

      // Upload assets to S3 using presigned URL
      await axios.put(
        preSignedResponse.url,
        file,
        { headers: { 'Content-Type': file.type } },
      );

      fetchAssetWorker.postMessage({
        id: preSignedResponse.id,
        token: api.defaults.headers.common.Authorization,
      });

      await new Promise((resolve, reject) => {
        const interval = setInterval(() => {
          const hasCompleted = loaded.includes(preSignedResponse.id);
          const hasFailed = loaded.includes(preSignedResponse.id);

          if (hasCompleted || hasFailed) {
            clearInterval(interval);
          }

          if (hasCompleted) {
            loaded = loaded.filter((n) => n !== preSignedResponse.id);
            resolve(preSignedResponse.id);
          }

          if (hasFailed) {
            failed = failed.filter((n) => n !== preSignedResponse.id);
            reject(preSignedResponse.id);
          }
        }, 1500);
      });

      return preSignedResponse;
    },

    /**
     * Make a GET request to check Asset optimize progress
     * status using Asset Id param.
     * @param id - Asset id
     * @returns - Asset progress data
     */
    async checkAssetProgress(id: string): Promise<AssetOptimizeProgress> {
      return requests.get(`/assets/${id}/progress`);
    },
  },
});

export default useAssetsStore;
